/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

/* eslint-disable import/order */

import _ from 'lodash';
import Radio from 'backbone.radio';
import AppRouter from 'marionette.approuter';

import Config from '@config/Config';

import Session from './Session';
import * as NavUtils from './util/NavUtils';
import importAll from './util/importAll';

import showInternalLoginPage from '../login/commands/ShowInternalLoginPage';
import showExternalLoginPage from '../login/commands/ShowExternalLoginPage';
import showDoDWarningPage from '../login/commands/ShowDoDWarningPage';
import showRegisterPage from '../login/commands/ShowRegisterPage';
import Cookies from 'js-cookie';

const mappings = importAll(require.context('../', true, /mappings.json$/));
const commands = importAll(require.context('../', true, /\/commands\/..*\.js$/));
const builders = importAll(require.context('../', true, /\/builders\/..*\.js$/));
const taskBuilders = importAll(require.context('../', true, /\/*Builder\.js$/));

Object.assign(builders, taskBuilders);

/**
 * Marionette now has its own copy of backbone within node_modules, so we need
 * to be careful about any place in the application where state is stored in the
 * Backbone object by Marionette.
 */
// var router = new Backbone.Router();
const router = new AppRouter();

for (const builderObj of Object.values(builders)) {
  const channelName = builderObj.channel || 'nonroutable';
  Radio.channel(channelName).reply(builderObj.event, builderObj.builder);
}

function setHeader() {
  const currentRoute = NavUtils.getCurrentRoute() || '';

  // Don't manipulate the header if coming from an empty route. The finishLogin
  // method will handling displaying the correct header.
  if (currentRoute === '') {
    return;
  }

  if (!Session.user) {
    Radio.channel('header').trigger('hide:header');
  } else if (!EXTERNAL) {
    Radio.channel('header').trigger('show:internal', currentRoute);
  } else if (EXTERNAL) {
    Radio.channel('header').trigger('show:external');
  }
}

async function applyCommand(command, ...args) {
  if (_.isFunction(command)) {
    await command.apply(this, ...args);
  }
}

// iterate over the contexts ('mappings' is the contexts' mappings.json file contents)
for (const mappingContents of Object.values(mappings)) {
  // iterate over the mappings for this context; each is something like 'show:dashboard:home'
  for (const [eventName, commandRouteMapping] of Object.entries(mappingContents)) {
    // grab the Command class for this mapping and register the radio event and route(s)
    const command = commands[`./${commandRouteMapping.command}.js`];

    const extendedCommand = async (...args) => {
      setHeader();

      if (commandRouteMapping.isPublic) {
        await applyCommand(command, args);
      } else if (EXTERNAL && !Session.user) {
        const samlArtifactCookieName = Config.getSamlArtifactCookieName();
        const samlArtCookie = Cookies.get(samlArtifactCookieName);

        if (commandRouteMapping.command === 'login/commands/ShowRegisterPage') {
          showRegisterPage();
        }
        // Route appropriately depending on whether a user has accepted the disclaimer already
        else if (!Session.disclaimerAccepted && !samlArtCookie) {
          showDoDWarningPage();
        } else {
          showExternalLoginPage();
        }
      } else if (!EXTERNAL && !Session.user) {
        if (!Session.disclaimerAccepted) {
          showDoDWarningPage();
        } else {
          showInternalLoginPage();
        }
      } else {
        await applyCommand(command, args);
      }
    };

    // Associate the event and the command on the hub.
    Radio.channel('navigate').reply(eventName, extendedCommand);

    // Associate the route and the command on the router.
    commandRouteMapping.routes.forEach((pattern) => {
      router.route(pattern, extendedCommand);
    });
  }
}
